import { useEffect, useState } from "react";
import {FolderOpen, Settings, LogOut, ChevronDown, Plus, ChevronLeft, ChevronRight, CircleAlert} from "lucide-react";
import logoAtomDark from "../../../assets/images/18a9c.svg";
import LayoutDashboard from "../icons/LayoutDashboardCustom";
import ExecutiveCommandCenter from "../../../assets/images/Executive-Command-Center.svg";
import OpportunityAction from "../../../assets/images/OpportunityAction.svg";
import SpendIntelligence from "../../../assets/images/SpendIntelligence.svg";
import SupplierMarket from "../../../assets/images/SupplierMarket.svg";
import nagotiationP from "../../../assets/images/nagotiationP.svg";
import costModel from "../../../assets/images/costModel.svg";
import ThirdParty from "../../../assets/images/ThirdParty.svg";
import ContractReview from "../../../assets/images/ContractR.svg";
import PRCreation from "../../../assets/images/PRCreation.svg";
import IAC from "../../../assets/images/IAC.svg";
import Receipt from "../../../assets/images/Receipt.svg";
import InvoiceNew from "../../../assets/images/InvoiceNew.svg";
import PaymentStatus from "../../../assets/images/PaymentStatus.svg";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import type { View } from "../../types";
import { useNavigate } from "react-router-dom";
import { auth, getAuthFromCookie } from "../../views/auth/auth";
import { useExcelData } from "../../../context/ExcelDataContext";

type SidebarIcon = React.ElementType | string;

interface Props {
  currentView: View;
  onNavigate: (v: View) => void;
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const SPEND_NAV_ITEMS = [
  { view: "executive-dashboard" as View, label: "Executive Command Center", icon: ExecutiveCommandCenter },
  { view: "projects" as View, label: "Projects", icon: FolderOpen },
  { view: "data-ingestion" as View, label: "Data Ingestion", icon: FolderOpen },
  { view: "spend-intelligence" as View, label: "Spend", icon: SpendIntelligence },
  { view: "supplier-market" as View, label: "Supplier & Market", icon: SupplierMarket },
  { view: "opportunity-insights" as View, label: "Opportunity & Action", icon: OpportunityAction },
];

const SOURCING_NAV_ITEMS = [
   { view: "cost-model-workflow" as View, label: "Cost Model Workflow", icon: costModel },
  { view: "negotiation-pack" as View, label: "Negotiation Pack", icon: nagotiationP },
  // { view: "supplier-risk-analysis" as View, label: "Third Party/Supplier Risk Management", icon: LayoutDashboard }
];

const RISK_NAV_ITEMS = [   // ← new array
  { view: "supplier-risk-analysis" as View, label: "Third Party/Supplier Risk Management", icon: ThirdParty },
];

const SUPPLIER_RISK_NAV_ITEMS = [
  { view: "receipt-creation" as View, label: "Receipt Creation", icon: Receipt },
  { view: "invoice-audit" as View, label: "Invoice Audit (3-way)", icon: InvoiceNew },
  { view: "payment-status-query" as View, label: "Payment Status Query", icon: PaymentStatus },
];
const CONTRACT_NAV_ITEMS = [
  { view: "contract-review" as View, label: "Contract Review", icon: ContractReview },
];
const PURCHASE_ORDER_NAV_ITEMS = [
  { view: "pr-creation" as View, label: "PR Creation", icon: PRCreation },
  // { view: "contract-recommender" as View, label: "Contract Recommender", icon: CircleAlert },
  { view: "iac-eligibility-engine" as View, label: "IAC Eligibility Engine", icon: IAC },
  { view: "po-mismatch-resolution" as View, label: "PO Mismatch Query", icon: CircleAlert },
];

const SECTIONS = [
  { id: "spend", items: SPEND_NAV_ITEMS },
  { id: "sourcing", items: SOURCING_NAV_ITEMS },
  { id: "risk", items: RISK_NAV_ITEMS },
  { id: "contract", items: CONTRACT_NAV_ITEMS },
  { id: "purchase", items: PURCHASE_ORDER_NAV_ITEMS },
  { id: "invoice-payment", items: SUPPLIER_RISK_NAV_ITEMS },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

function getSectionForView(view: View): SectionId | null {
  const section = SECTIONS.find((s) => s.items.some((item) => item.view === view));
  return section ? section.id : null;
}

function renderSidebarIcon(Icon: SidebarIcon, alt: string) {
  if (typeof Icon === "string") {
    return <img src={Icon} alt="" className="sidebar-nav__icon sidebar-nav__icon-img" aria-hidden="true" />;
  }
  return <Icon size={15} className="sidebar-nav__icon" aria-hidden={true} />;
}

export function Sidebar({ currentView, onNavigate, isOpen, onClose, isCollapsed, onToggleCollapse }: Props) {
  const [expandedSection, setExpandedSection] = useState<SectionId | null>(
    () => getSectionForView(currentView) ?? "spend"
  );

  // Whichever section holds the active page should be the one that's open —
  // e.g. after a refresh, or after navigating to a view outside the sidebar.
  useEffect(() => {
    const section = getSectionForView(currentView);
    if (section) setExpandedSection(section);
  }, [currentView]);

  const toggleSection = (id: SectionId) => {
    setExpandedSection((prev) => (prev === id ? null : id));
  };

  const { analysisResult } = useExcelData();
  const costModelEnabled = !!analysisResult;

  const navigate = useNavigate();
  const signedInUser = getAuthFromCookie();
  const signedInUserName =
    [signedInUser?.firstname, signedInUser?.lastname].filter(Boolean).join(" ").trim() ||
    signedInUser?.email?.split("@")[0] ||
    "User";
  const signedInUserInitials =
    signedInUserName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "U";

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate("/login", {
        replace: true,
      });
    } catch (err) {
      console.error(
        "Logout failed",
        err
      );
    }
  };

  const isActive = (view: View) => {
    return currentView === view;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay is-visible"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`sidebar${isOpen ? " is-open" : ""}${isCollapsed ? " is-collapsed" : ""}`}
        aria-label="Main navigation"
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand__monogram" aria-hidden="true">
            <img alt="procure.ai" className="size-full" src={logoAtomDark} />
          </div>
          {!isCollapsed && (
            <div className="login-left-brand-text">
              <span className="login-left-brand-procure">Procur</span>
              <span className="login-left-brand-ai">AI</span>
            </div>
          )}
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>
        {/* Main nav */}
        {!isCollapsed && (
          <div className="sidebarMenuWrapper">
            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("spend")}
              aria-expanded={expandedSection === "spend"}
            >
              <span>Spend</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "spend" && (
              <nav className="sidebar-nav" aria-label="Spend intelligence">
                {SPEND_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}

            
            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("sourcing")}
              aria-expanded={expandedSection === "sourcing"}
            >
              <span>Sourcing</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "sourcing" && (
              <nav className="sidebar-nav" aria-label="Sourcing">
                {SOURCING_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}



            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("risk")}
              aria-expanded={expandedSection === "risk"}
            >
              <span>Risk</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "risk" && (
              <nav className="sidebar-nav" aria-label="Risk">
                {RISK_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}

            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("contract")}
              aria-expanded={expandedSection === "contract"}
            >
              <span>Contract</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "contract" && (
              <nav className="sidebar-nav" aria-label="Contract">
                {CONTRACT_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}

            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("purchase")}
              aria-expanded={expandedSection === "purchase"}
            >
              <span>Purchase</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "purchase" && (
              <nav className="sidebar-nav" aria-label="Purchase order">
                {PURCHASE_ORDER_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}
            <button
              type="button"
              className="sidebar-section-label"
              onClick={() => toggleSection("invoice-payment")}
              aria-expanded={expandedSection === "invoice-payment"}
            >
              <span>INVOICE & PAYMENT</span>
              <ChevronDown size={12} />
            </button>

            {expandedSection === "invoice-payment" && (
              <nav className="sidebar-nav" aria-label="INVOICE & PAYMENT">
                {SUPPLIER_RISK_NAV_ITEMS.map(({ view, label, icon: Icon }) => (
                  <button
                    key={view}
                    type="button"
                    className={`sidebar-nav__item${isActive(view) ? " is-active" : ""}`}
                    onClick={() => { onNavigate(view); onClose(); }}
                    aria-current={isActive(view) ? "page" : undefined}
                  >
                    {renderSidebarIcon(Icon, label)}
                    {label}
                  </button>
                ))}
              </nav>
            )}
          </div>
        )}

        {isCollapsed && (
          <nav className="sidebar-nav" aria-label="Main">
            {[...SPEND_NAV_ITEMS, ...SOURCING_NAV_ITEMS, ...RISK_NAV_ITEMS, ...PURCHASE_ORDER_NAV_ITEMS, ...CONTRACT_NAV_ITEMS, ...SUPPLIER_RISK_NAV_ITEMS].map(({ view, label, icon: Icon }) => {
              const isCostModel = view === "cost-model-analysis";
              const isDisabled = isCostModel && !costModelEnabled;
              const tooltipText = isDisabled ? "Run Cost Model Analysis first to unlock" : label;
              return (
                <Tooltip key={view}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={`sidebar-nav__item${isActive(view) ? " is-active" : ""} is-icon-only${isDisabled ? " is-disabled" : ""}`}
                      onClick={() => { if (!isDisabled) { onNavigate(view); onClose(); } }}
                      aria-current={isActive(view) ? "page" : undefined}
                      disabled={isDisabled}
                      aria-label={tooltipText}
                    >
                      {renderSidebarIcon(Icon, label)}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8} className="!px-[9px] !py-[3px]">
                    {tooltipText}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </nav>
        )}

        {/* Footer */}
        <div className="sidebar-footer">
          <div className={`sidebar-user${isCollapsed ? " sidebar-user--collapsed" : ""}`}>
            <div className="sidebar-user__avatar" aria-hidden="true" title={`${signedInUserName} — Procurement Admin`}>{signedInUserInitials}</div>
            {!isCollapsed && (
              <>
                <div>
                  <div className="sidebar-user__name">{signedInUserName}</div>
                  <div className="sidebar-user__role">Procurement Admin</div>
                </div>
                <button
                  type="button"
                  className="sidebar-user__logout"
                  aria-label="Log out"
                  title="Logout"
                  onClick={handleLogout}
                >
                  <LogOut size={14} />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
